import moment from 'moment'
import styles from './styles.module.scss'
import IFace from '../../ViewerIFace'
import React from 'react'
import IcDelete from 'emailThreadViewer/icons/IcDelete'
import NameOrEmailList from './NameOrEmailList'
import IcCalendar from 'emailThreadViewer/icons/IcCalendar'
import IcAddCalendar from 'emailThreadViewer/icons/IcAddCalendar'
import { Tooltip } from 'antd'
import i18n from 'i18n'
import { linkifyMailNode } from '../../helper/linkifyMail'

interface Attendee {
  name?: string | null
  email?: string | null
}

interface EventItem {
  id: string
  title?: string | null
  location?: string | null
  start_date?: string | null
  end_date?: string | null
  all_day?: boolean
  description?: string | null
  meeting_link?: string | null
  attendees?: Attendee[]
  past_event?: boolean
}

interface EventsProps {
  events?: EventItem[]
  mailId: string
}

interface EventProps {
  event: EventItem
  mailId: string
}

interface DateTimeProps {
  startDate?: string | null
  endDate?: string | null
  allDay?: boolean
}

interface DescriptionProps {
  mailId: string
  description?: string | null
}

const Events: React.FC<EventsProps> = ({ events, mailId }) => {
  if (!events?.length) return null

  return (
    <div>
      {events.map((event, idx) => (
        <Event event={event} mailId={mailId} key={`${mailId}-${idx}-${event.id}`} />
      ))}
    </div>
  )
}

const Event: React.FC<EventProps> = ({ event, mailId }) => {
  const {
    id,
    title = null,
    location = null,
    start_date: startDate = null,
    end_date: endDate = null,
    all_day: allDay,
    description = null,
    meeting_link: meetingLink = null,
    attendees,
    past_event: pastEvent = false,
  } = event

  const handleDelete = (): void => IFace.WebToNative.changeEventStatus(mailId, id, 'ignore')
  const handleAddToCalendar = (): void => IFace.WebToNative.addToEvent(mailId, id)

  return (
    <div className={styles.eventItem}>
      <div className={styles.symbolContainer}>
        <IcCalendar style={{ opacity: pastEvent ? 0.5 : 1 }} />
      </div>
      <div className={styles.eventItemContent}>
        <div className={styles.eventItemTitle}>
          {title}
          <div className={styles.eventItemTitleActionContainer}>
            <Tooltip title={i18n.t('email.aiExtraction.addToEvent')}>
              <button className={styles.iconButton} onClick={handleAddToCalendar}>
                <IcAddCalendar />
              </button>
            </Tooltip>
            <Tooltip title={i18n.t('email.aiExtraction.delete')}>
              <button className={styles.iconButton} onClick={handleDelete}>
                <IcDelete />
              </button>
            </Tooltip>
          </div>
        </div>
        <DateTime startDate={startDate} endDate={endDate} allDay={allDay} />
        <NameOrEmailList nameOrEmails={attendees} />
        {location && <div className={styles.eventItemLocation}>{location}</div>}
        {meetingLink && (
          <div className={styles.eventItemMeetingLink}>
            <a href={meetingLink} target="_blank" rel="noreferrer">
              {meetingLink}
            </a>
          </div>
        )}
        <Description mailId={mailId} description={description} />
      </div>
    </div>
  )
}

const DateTime: React.FC<DateTimeProps> = ({ startDate, endDate, allDay }) => {
  const { dateFormat, timeFormat } = IFace.getTimeFormats()

  if (!startDate && !endDate) {
    return null
  }

  if (allDay) {
    return (
      <div className={styles.eventItemDatetime}>
        {moment(startDate || endDate).format(dateFormat)}
      </div>
    )
  }

  const startDateString = startDate ? moment(startDate).format(`${dateFormat} ${timeFormat}`) : ''
  const isSameDay = startDate && moment(startDate).isSame(moment(endDate), 'day')
  const endDateString = endDate ? moment(endDate).format(isSameDay ? timeFormat : dateFormat) : ''

  return <div className={styles.eventItemDatetime}>{`${startDateString} → ${endDateString}`}</div>
}

const Description: React.FC<DescriptionProps> = ({ mailId, description }) => {
  if (!description) {
    return null
  }

  const setRef = (node: HTMLDivElement | null): void => {
    if (node != null) linkifyMailNode(node)
  }

  return (
    <div
      className={styles.eventItemDescription}
      ref={setRef}
      onClick={(e: React.MouseEvent) => {
        const linkEl = (e.target as HTMLElement).closest('a')
        if (!linkEl) return

        e.preventDefault()
        e.stopPropagation()
        IFace.WebToNative.openURL(mailId, linkEl.href)
      }}
    >
      {description}
    </div>
  )
}

export default Events
